import React from 'react';
import { cn } from '../../lib/utils';
import { useI18n } from '../../i18n/I18nContext.jsx';

function getNavSections(t) {
  return [
    {
      items: [
        { id: 'dashboard', label: t('nav.dashboard') || 'Dashboard', path: '/dashboard' },
        { id: 'analytics', label: t('nav.analytics'), path: '/analytics', requiresManager: true },
      ],
    },
    {
      label: t('nav.management') || 'Management',
      requiresManager: true,
      items: [
        { id: 'menu', label: t('nav.menu'), path: '/menu', requiresManager: true },
        { id: 'inventory', label: t('nav.inventory'), path: '/inventory', requiresManager: true },
        { id: 'suppliers', label: t('nav.suppliers') || 'Suppliers', path: '/suppliers', requiresManager: true },
        { id: 'deliveries', label: t('nav.deliveries') || 'Deliveries', path: '/deliveries', requiresManager: true },
        { id: 'staff', label: t('nav.staff'), path: '/staff', requiresManager: true },
        { id: 'discounts', label: t('nav.discounts'), path: '/discounts', requiresManager: true },
        { id: 'reservations', label: t('nav.reservations'), path: '/reservations' },
        { id: 'expenses', label: t('nav.expenses'), path: '/expenses', requiresManager: true },
      ],
    },
    {
      label: t('nav.reportsSection') || 'Reports',
      requiresManager: true,
      items: [
        { id: 'reports', label: t('nav.reports') || 'Reports', path: '/reports', requiresManager: true },
      ],
    },
    {
      label: 'Account',
      ownerOnly: true,
      items: [
        { id: 'account',              label: 'Overview',     path: '/account',              ownerOnly: true },
        { id: 'account-subscription', label: 'Subscription', path: '/account/subscription', ownerOnly: true },
        { id: 'account-addons',       label: 'Add-ons',      path: '/account/addons',       ownerOnly: true },
        { id: 'account-venues',       label: 'Venues',       path: '/account/venues',       ownerOnly: true },
      ],
    },
    {
      items: [
        { id: 'settings', label: t('nav.settings'), path: '/settings', requiresManager: true },
      ],
    },
  ];
}

export function BackOfficeSidebar({ activeSection, staff, onNavigate, isOpen, onClose }) {
  const { t } = useI18n();

  const isManager = staff && (
    ['manager', 'owner', 'admin'].includes(staff.role?.toLowerCase()) ||
    (Array.isArray(staff.roles) && staff.roles.some(r => {
      const roleCode = (r?.code || r || '').toUpperCase();
      return ['MANAGER', 'OWNER', 'ADMIN', 'AUDITOR_READONLY'].includes(roleCode);
    }))
  );

  const isOwner = staff && ['owner', 'admin'].includes(staff.role?.toLowerCase());

  const sections = getNavSections(t);

  const isControlled = typeof isOpen === 'boolean';
  const asideClasses = isControlled
    ? cn(
        'sidebar transition-all duration-brand ease-brand bg-carbon border-r border-[#2a2a2e] flex flex-col overflow-hidden h-full fixed inset-0 z-40 md:static md:inset-auto md:flex',
        isOpen ? 'sidebar-open w-full md:w-64' : 'sidebar-closed w-0 md:w-0'
      )
    : cn('hidden md:flex flex-col w-64 bg-carbon border-r border-[#2a2a2e]');

  const contentClasses = cn(
    'flex-1 flex flex-col min-h-0',
    isControlled && !isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100 pointer-events-auto'
  );

  return (
    <aside className={asideClasses} aria-hidden={isControlled ? !isOpen : false}>
      <div className={contentClasses}>
        <div className={cn('p-6 flex items-center justify-between', isControlled && !isOpen && 'p-0')}>
          <h1 className="text-2xl font-bold text-signal">Quantbar</h1>
          {isControlled && isOpen && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="md:hidden p-2 rounded-btn text-ash hover:text-linen hover:bg-void transition-colors duration-brand"
              aria-label="Close menu"
            >
              <span className="text-xl leading-none">✕</span>
            </button>
          )}
        </div>
        <nav className="flex-1 px-4 space-y-4 overflow-y-auto">
          {sections.map((section, sIdx) => {
            // Filter items by role
            const visibleItems = section.items.filter(
              (item) => (!item.requiresManager || isManager) && (!item.ownerOnly || isOwner)
            );
            if (visibleItems.length === 0) return null;
            if (section.requiresManager && !isManager) return null;
            if (section.ownerOnly && !isOwner) return null;

            return (
              <div key={sIdx}>
                {section.label && (
                  <p className="px-4 mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ash">
                    {section.label}
                  </p>
                )}
                {visibleItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onNavigate?.(item.path)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 mb-1 rounded-card text-left transition-colors duration-brand',
                      activeSection === item.id
                        ? 'bg-void text-linen'
                        : 'text-ash hover:bg-void/60 hover:text-linen'
                    )}
                  >
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
